import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Ohm's Law — battery + zigzag resistor + ammeter, electron-flow arrows on conductors. */
export function DiagramVoltsAmpsOhms() {
  // Loop bounds
  const lx = 70, ly = 70, lw = 220, lh = 130;

  // Zigzag resistor on top wire
  const zigStart = lx + 60, zigEnd = lx + lw - 60;
  const zigW = zigEnd - zigStart;
  const zigPts = Array.from({ length: 9 }).map((_, i) => {
    const x = zigStart + (zigW / 8) * i;
    const y = ly + (i % 2 === 0 ? 0 : (i % 4 === 1 ? -10 : 10));
    return `${x},${y}`;
  });

  return (
    <DiagramCanvas>
      {/* Wires (rectangle minus gaps) */}
      <Line x1={lx} y1={ly} x2={zigStart} y2={ly} stroke={DT.stroke} strokeWidth="2" />
      <Line x1={zigEnd} y1={ly} x2={lx + lw} y2={ly} stroke={DT.stroke} strokeWidth="2" />
      <Line x1={lx + lw} y1={ly} x2={lx + lw} y2={ly + lh / 2 - 14} stroke={DT.stroke} strokeWidth="2" />
      <Line x1={lx + lw} y1={ly + lh / 2 + 14} x2={lx + lw} y2={ly + lh} stroke={DT.stroke} strokeWidth="2" />
      <Line x1={lx + lw} y1={ly + lh} x2={lx} y2={ly + lh} stroke={DT.stroke} strokeWidth="2" />
      <Line x1={lx} y1={ly + lh} x2={lx} y2={ly + lh / 2 + 12} stroke={DT.stroke} strokeWidth="2" />
      <Line x1={lx} y1={ly + lh / 2 - 12} x2={lx} y2={ly} stroke={DT.stroke} strokeWidth="2" />

      {/* Battery (left side) */}
      <Line x1={lx - 12} y1={ly + lh / 2 - 12} x2={lx + 12} y2={ly + lh / 2 - 12} stroke={DT.stroke} strokeWidth="3.5" />
      <Line x1={lx - 6} y1={ly + lh / 2 - 4} x2={lx + 6} y2={ly + lh / 2 - 4} stroke={DT.stroke} strokeWidth="2" />
      <Line x1={lx - 12} y1={ly + lh / 2 + 4} x2={lx + 12} y2={ly + lh / 2 + 4} stroke={DT.stroke} strokeWidth="3.5" />
      <Line x1={lx - 6} y1={ly + lh / 2 + 12} x2={lx + 6} y2={ly + lh / 2 + 12} stroke={DT.stroke} strokeWidth="2" />
      <SvgText x={lx - 18} y={ly + lh / 2 - 14} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="end" fontWeight="700">+</SvgText>
      <SvgText x={lx - 18} y={ly + lh / 2 + 18} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="end" fontWeight="700">−</SvgText>

      {/* Zigzag resistor */}
      <Path d={`M${zigPts.join(' L')}`} fill="none" stroke={DT.varColor} strokeWidth="2" />
      <SvgText x={(zigStart + zigEnd) / 2} y={ly - 16} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">R</SvgText>

      {/* Ammeter on right side */}
      <Circle cx={lx + lw} cy={ly + lh / 2} r={14} fill={DT.fill} stroke={DT.varColor} strokeWidth="1.6" />
      <SvgText x={lx + lw} y={ly + lh / 2 + 4} fill={DT.varColor} fontSize="11" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">A</SvgText>

      {/* Voltmeter across battery (parallel) */}
      <Line x1={lx - 28} y1={ly + lh / 2 - 12} x2={lx - 40} y2={ly + lh / 2 - 12} stroke={DT.leader} strokeWidth="1" />
      <Line x1={lx - 28} y1={ly + lh / 2 + 12} x2={lx - 40} y2={ly + lh / 2 + 12} stroke={DT.leader} strokeWidth="1" />
      <Line x1={lx - 40} y1={ly + lh / 2 - 12} x2={lx - 40} y2={ly + lh / 2 + 12} stroke={DT.leader} strokeWidth="1" />
      <Circle cx={lx - 50} cy={ly + lh / 2} r={11} fill={DT.fill} stroke={DT.leader} strokeWidth="1.4" />
      <SvgText x={lx - 50} y={ly + lh / 2 + 4} fill={DT.leader} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">V</SvgText>

      {/* Current direction arrows on the loop */}
      {[
        { x: lx + 30, y: ly, dx: 1, dy: 0 },
        { x: lx + lw, y: ly + 30, dx: 0, dy: 1 },
        { x: lx + lw - 30, y: ly + lh, dx: -1, dy: 0 },
        { x: lx, y: ly + lh - 30, dx: 0, dy: -1 },
      ].map((p, i) => (
        <Polygon key={i}
          points={`${p.x + p.dx * 4},${p.y + p.dy * 4} ${p.x - p.dy * 4},${p.y + p.dx * 4} ${p.x + p.dy * 4},${p.y - p.dx * 4}`}
          fill={DT.arrow} />
      ))}

      {/* Labels */}
      <LeaderLine x1={lx - 50} y1={ly + lh / 2 - 14} x2={20} y2={50} varName="V" desc="Volts" anchor="start" />
      <LeaderLine x1={(zigStart + zigEnd) / 2} y1={ly + 6} x2={(zigStart + zigEnd) / 2} y2={210} varName="R" desc="Ohms (Ω)" anchor="middle" />
      <LeaderLine x1={lx + lw + 16} y1={ly + lh / 2} x2={310} y2={210} varName="I" desc="Amps" anchor="end" />

      <FormulaTag formula="V = I · R   →   I = V/R   R = V/I" />
    </DiagramCanvas>
  );
}
